import { gql } from 'graphql-request';
import useSWRMutation from 'swr/mutation';
import { captureMessage } from '@sentry/nextjs';
import { BasketFragment } from '@graphql/fragments';
import { productAdded, productRemoved } from '@repo/ui/src/services/analytics';
import { basketLineInputToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { basketRequest, OutOfStockError } from '@hooks/basket/_utils';
import { useCustomerIdToken } from '@hooks/customer';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ModalID } from '@repo/ui/src/modules/UI';
import { useUI } from '@repo/ui/src/modules/UI/hooks/useUI';
import { BasketData } from '@repo/ui/src/types/basket';
import { GraphQLRequestWithTokenProps } from '@repo/ui/src/utils/graphql-request';
import { formatBasket, useBasketMutation } from '@hooks/basket/useBasket';
import { LineItem } from '@repo/ui/src/types/lineItem';

export interface OptionItem {
  optionId: string;
  optionValueId: string;
}

interface Arguments {
  lineItem: LineItem;
  optionItems?: Array<OptionItem>;
  quantity: number;
}

const logCartEvents = {
  productAdded,
  productRemoved,
};

interface Key extends GraphQLRequestWithTokenProps {
  locale: string;
}

const getBasketItem = ({ lineItem, quantity, optionItems }: Arguments) =>
  quantity
    ? {
        itemId: lineItem.id,
        mainImageUrl: lineItem.image,
        productId: lineItem.sku,
        quantity,
        category: lineItem.category,
        description: lineItem.description,
        optionItems: optionItems?.map((option) => ({
          id: option.optionId,
          valueId: option.optionValueId,
        })),
        options: lineItem.productOptions ? JSON.stringify(lineItem.productOptions) : '',
      }
    : // temporary fix for removing items that are out of stock
      {
        itemId: lineItem.id,
        productId: lineItem.sku,
        quantity,
      };

const updateProduct = async (
  props: Key,
  lineItem: LineItem,
  quantity: number,
  optionItems?: OptionItem[],
) => {
  const { basket } = (await basketRequest<{ basket: BasketData }>({
    ...props,
    variables: { item: getBasketItem({ lineItem, quantity, optionItems }) },
  }))!;

  return basket;
};

const fetcher = async (
  props: Key,
  { arg: { lineItem, quantity, optionItems } }: { arg: Arguments },
) => {
  const basket = await updateProduct(props, lineItem, quantity, optionItems);
  const type = quantity < lineItem.quantity ? 'productRemoved' : 'productAdded';

  logCartEvents[type]({
    ...basketLineInputToAnalytics(lineItem),
    list_name: 'cart',
    basket_token: basket.basketJWT,
    affiliation: 'website',
    locale: props.locale,
    brand: 'Traeger',
    cart_id: basket.id,
    currency: basket.currency,
  });
  return basket;
};

export const patchBasketItem = gql`
  ${BasketFragment}
  mutation PatchBasketItem($item: BasketItemInput!) {
    basket: patchBasketItem(item: $item) {
      ...Basket
    }
  }
`;

const useUpdateBasket = () => {
  const { locale } = useCurrentUrl();
  const mutate = useBasketMutation();
  const idToken = useCustomerIdToken();
  const { pushModal, closeModal } = useUI();

  return useSWRMutation({ locale, document: patchBasketItem, idToken }, fetcher, {
    onSuccess: (data) => {
      const basket = formatBasket(data, locale);
      mutate(basket, { revalidate: false });
    },
    onError(error: Error) {
      closeModal(ModalID.cart);
      if (error instanceof OutOfStockError && error.info) {
        pushModal(ModalID.outOfStock, error.info);
      } else {
        captureMessage('Patch Cart Error', {
          level: 'error',
          extra: {
            message: error.message,
          },
        });
        pushModal(ModalID.error);
      }
    },
    throwOnError: false,
  });
};

export default useUpdateBasket;
